import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Input,
  Modal,
  Progress,
  Segmented,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  LeftOutlined,
  PlusOutlined,
  ReloadOutlined,
  RightOutlined,
  RiseOutlined,
  SaveOutlined,
  SearchOutlined,
  SettingOutlined,
  UnorderedListOutlined,
  UploadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import ProjectPageShell, { useProjectContext } from "../shared/ProjectPageShell";
import StatCard from "../shared/StatCard";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import { activityExecutionApis, executionNormApis } from "../../../api/ActivityExecution";
import adminOrgApis from "../../../api/master/adminOrg";
import {
  CalendarDayEntry,
  CalendarSection,
  DaySheetScope,
  ExecutionCalendarMonth,
  normaliseCalendarMonth,
  siteDailyApis,
} from "../../../api/SiteDaily";
import { useWbsRecordName } from "../shared/useWbsCodes";
import { fmtQty, toDateOnly } from "./executionNorm";
import { splitWbsCode } from "../shared/wbsCode";
import DailyExecutionDrawer from "./DailyExecutionDrawer";
import CivilUploadModal from "./uploads/CivilUploadModal";
import MaterialIssueUploadModal from "./uploads/MaterialIssueUploadModal";
import EquipmentLogUploadModal from "./uploads/EquipmentLogUploadModal";
import StoreStockUploadModal from "./uploads/StoreStockUploadModal";
import ExecutionCalendar from "./calendar/ExecutionCalendar";
import CalendarDayDrawer from "./calendar/CalendarDayDrawer";
import { shiftMonth } from "./calendar/calendarModel";
import {
  DayApprovalActions,
  DayApprovalBar,
  RejectedDaysAlert,
  useDayApprovalStates,
} from "../dailyApprovals/DayApprovalBar";
import "../dailyApprovals/dailyApprovals.css";
import MaterialStoresSection from "./sections/MaterialStoresSection";
import MechanicalSection from "./sections/MechanicalSection";
import MuckingPrwSection from "./sections/MuckingPrwSection";
import StoreStockSection from "./sections/StoreStockSection";
import StockRegisterSection from "./sections/StockRegisterSection";
import { EMPTY_SHEET_META, SheetSectionHandle, SheetSectionMeta } from "./sections/daySheet";
import "./execution.css";

// Daily Execution — three sections, one page.
//
//   Civil            the execution REGISTER (date -> phase -> face), captured
//                    in a drawer. The register IS the section.
//   Material-Stores  what the stores clerk issued to each task today.
//   Mechanical       hours, fuel and meter readings per machine per task.
//
// The section lives in the URL (?section=civil|material|mechanical) so a link
// lands on the right sheet and the browser's back button means something.
// Material and Mechanical share ONE working date and scope: the stores clerk
// and the plant clerk are recording the same day.
//
// Capture happens in a drawer, deliberately. An always-open form made the
// screen read as half-filled-in the moment you arrived, with no clear line
// between browsing what has been recorded and recording something new. Now the
// page answers "what has been captured?" and one button starts the other job.

// DAYS per page, not entries. The register groups by date, so paging by entry
// would slice a day in half and show a date whose totals did not add up to the
// rows beneath it.
const PAGE_SIZE = 20;

// NO MONEY ON THIS SCREEN. Daily Execution is a site data-entry screen worked by
// ground-level executives; contract values and daily earned value are not theirs
// to see. Progress is expressed as a PERCENTAGE only.
//
// The percentage is still money-weighted underneath — it has to be, because the
// BOQ lines under a face are in TON, CUM, m, kg and MT and their quantities
// cannot be added together — but the rupee figures behind it never reach the
// page.

const SECTIONS = ["civil", "material", "mechanical", "stock", "cost"] as const;
type Section = (typeof SECTIONS)[number];
const isSection = (value: string | null): value is Section =>
  SECTIONS.includes(value as Section);

// Register | Calendar. The calendar answers "which days were entered and which
// were missed?" for a month; the register and sheets stay where entry happens.
type View = "register" | "calendar";
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

const DailyExecutionPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projectId: contextProjectId, projectName } = useProjectContext();
  const projectId = Number(params.projectId) || contextProjectId;

  // ---- section (URL-synced) ---------------------------------------------
  const sectionParam = searchParams.get("section");
  const section: Section = isSection(sectionParam) ? sectionParam : "civil";
  const setSection = (next: Section) => {
    const query = new URLSearchParams(searchParams);
    query.set("section", next);
    // `state` is carried over on purpose: it holds the project context, and
    // setSearchParams would otherwise drop it.
    setSearchParams(query, { replace: true, state: location.state });
  };

  // ---- view + calendar month (URL-synced) --------------------------------
  const view: View = searchParams.get("view") === "calendar" ? "calendar" : "register";
  const isCalendar = view === "calendar";
  const monthParam = searchParams.get("month") || "";
  const calendarMonth = MONTH_RE.test(monthParam) ? monthParam : dayjs().format("YYYY-MM");
  const updateQuery = (patch: Record<string, string | null>) => {
    const query = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => (value === null ? query.delete(key) : query.set(key, value)));
    setSearchParams(query, { replace: true, state: location.state });
  };
  const setCalendarMonth = (month: string) => updateQuery({ month });

  const [calendarData, setCalendarData] = useState<ExecutionCalendarMonth | null>(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState("");
  const [calendarFocus, setCalendarFocus] = useState<CalendarSection>("all");
  const [calendarTick, setCalendarTick] = useState(0);
  // A stat card can spotlight one state on the grid (click again to clear).
  const [focusState, setFocusState] = useState<string | null>(null);
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  // Date a new entry starts on when "Add execution" comes from a calendar day.
  const [addDate, setAddDate] = useState<string | undefined>();

  // ---- civil register ----------------------------------------------------
  const [dates, setDates] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  // Percentage only — the planned/executed rupee figures the API also returns are
  // deliberately not held in state on this screen.
  const [projectTotals, setProjectTotals] = useState({ percent: "" });
  const [pageNo, setPageNo] = useState(1);
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [phaseFilter, setPhaseFilter] = useState<number | undefined>();
  const [activityFilter, setActivityFilter] = useState<number | undefined>();
  const [activities, setActivities] = useState<any[]>([]);
  // Task Monitoring is configured on this project: the register then shows
  // tracked / derived line counts and the activity options carry a badge.
  const [monitoringConfigured, setMonitoringConfigured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  // ---- day sheets (Material-Stores / Mechanical) -------------------------
  const [sheetDate, setSheetDate] = useState<string>(() => toDateOnly(new Date()));
  // Material, Mechanical and Mucking & PRW are booked against civil-completed
  // TASKS, never a chosen day: the entry is stamped with today and the server
  // allocates it to the civil work it covers. So these sections always sit on
  // today — no date picker, and switching in from Store-Stock snaps back.
  const isTaskBooking = section === "material" || section === "mechanical" || section === "cost";
  useEffect(() => {
    if (!isTaskBooking) return;
    const today = toDateOnly(new Date());
    if (sheetDate !== today) setSheetDate(today);
  }, [isTaskBooking, sheetDate]);
  const [absBusy, setAbsBusy] = useState(false);
  const absUser = useSelector((state: any) => state.currentUser?.data);

  // The day's whole cost abstract as the client's ABS workbook. Reads the same
  // review payload the approval screen reads, so the two always agree; it is
  // fetched on demand rather than held, because nothing else on this page needs
  // it and the day can change under the user at any time.
  const downloadAbs = async () => {
    if (!projectId || !sheetDate) return;
    setAbsBusy(true);
    try {
      const api = await import("../../../api/DailyApprovals");
      const res = await api.dailyApprovalApis.review(projectId, sheetDate);
      if (res?.status !== true) {
        message.error(res?.message || "Could not read the day");
        return;
      }
      const review = api.normaliseReview(res.data);
      if (!review.cost.length) {
        message.info("Nothing costed on this day yet — enter material, machines, mucking or PRW first");
        return;
      }
      const helpers = await import("../reports/shared/excelHelpers");
      const logo = await helpers.fetchLogoBase64(absUser?.orgLogo);
      const { default: exportAbsWorkbook } = await import("../dailyApprovals/absWorkbook");
      await exportAbsWorkbook({ review, orgName: absUser?.orgName, logo });
      message.success("Cost abstract exported");
    } catch (err) {
      console.error("Error exporting the daily cost abstract:", err);
      message.error("Could not build the workbook");
    } finally {
      setAbsBusy(false);
    }
  };
  const [sheetScope, setSheetScope] = useState<DaySheetScope>("MONITORED");
  const [sheetMeta, setSheetMeta] = useState<SheetSectionMeta>(EMPTY_SHEET_META);
  const [dirtyCount, setDirtyCount] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);
  const [savingAll, setSavingAll] = useState(false);
  const [uomOptions, setUomOptions] = useState<Array<{ value: number; label: string }>>([]);
  const sectionRef = useRef<SheetSectionHandle>(null);
  // Which tab's Excel upload is open — one dialog per section.
  const [uploadOpen, setUploadOpen] = useState<Section | null>(null);
  // Store-Stock: the search lives in the page because it sits in the ONE strip
  // the tab has, beside the date and the approval actions; the section does the
  // filtering. The register is the same ledger read day by day over a range.
  const [stockSearch, setStockSearch] = useState("");
  const [stockView, setStockView] = useState<"entry" | "register">("entry");

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({ ...item, linkState: location.state }))
      )
    );
  }, [dispatch, location]);

  // A section swap unmounts the previous sheet; its summary and dirty count
  // must not linger on the new one.
  useEffect(() => {
    setSheetMeta(EMPTY_SHEET_META);
    setDirtyCount(0);
  }, [section]);

  // The date the tracked-line badge on each face is judged on: today for the
  // Civil register, the sheet's working date for Material / Mechanical.
  const badgeDate = section === "civil" ? toDateOnly(new Date()) : sheetDate;

  // Reloaded when the drawer closes: configuring norms from inside it can
  // change how many faces are ready to report. Asked as of the badge date so
  // the activity options can say how many lines each face has tracked then.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    executionNormApis
      .listActivities(projectId, { asOfDate: badgeDate })
      .then((res) => {
        if (cancelled || res?.status !== true) return;
        setActivities(res.data?.activities || []);
        setMonitoringConfigured(res.data?.monitoringConfigured === true);
      })
      .catch(() => !cancelled && setError("Could not load activities"));
    return () => {
      cancelled = true;
    };
  }, [projectId, drawerOpen, badgeDate]);

  // The material UOM select must offer uom_master ids — the id space the
  // material line's uomId lives in (buyer_materials.unitOfMeasure). NOT the
  // task form's work_uom_master list, whose workUomId is a different table.
  // Same source MachineryNormModal uses. Fetched once, when the sheet opens.
  useEffect(() => {
    if (section !== "material" || uomOptions.length) return;
    let cancelled = false;
    adminOrgApis
      .fetchUom()
      .then((res) => {
        if (cancelled || res?.status !== true || !Array.isArray(res.data)) return;
        setUomOptions(
          res.data
            .map((item: any) => ({
              value: Number(item?.id ?? item?.uomId),
              label: item?.uomShortCode
                ? `${item?.uomName} (${item?.uomShortCode})`
                : String(item?.uomName || ""),
            }))
            .filter((opt: { value: number }) => Number.isFinite(opt.value) && opt.value > 0)
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [section, uomOptions.length]);

  const load = useCallback(() => {
    if (!projectId || section !== "civil" || isCalendar) return;
    let cancelled = false;
    setLoading(true);
    activityExecutionApis
      .list(projectId, {
        pageNo,
        pageSize: PAGE_SIZE,
        phaseId: phaseFilter,
        activityId: activityFilter,
        fromDate: range?.[0] ? toDateOnly(range[0].toDate()) : undefined,
        toDate: range?.[1] ? toDateOnly(range[1].toDate()) : undefined,
      })
      .then((res) => {
        if (cancelled) return;
        if (res?.status !== true) {
          setError(res?.message || "Could not load the execution register");
          return;
        }
        setError("");
        setDates(res.data?.dates || []);
        setTotal(res.data?.total || 0);
        setProjectTotals({ percent: res.data?.projectTillDatePercent || "" });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, section, isCalendar, pageNo, range, phaseFilter, activityFilter]);

  useEffect(() => {
    if (!projectId || !isCalendar) return;
    let cancelled = false;
    setCalendarLoading(true);
    siteDailyApis
      .calendarMonth(projectId, {
        month: calendarMonth,
        phaseId: phaseFilter,
        activityId: activityFilter,
        section: calendarFocus,
      })
      .then((res) => {
        if (cancelled) return;
        if (res?.status !== true) {
          setCalendarError(res?.message || "Could not load the calendar");
          return;
        }
        setCalendarError("");
        setCalendarData(normaliseCalendarMonth(res.data));
      })
      .finally(() => !cancelled && setCalendarLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, isCalendar, calendarMonth, phaseFilter, activityFilter, calendarFocus, calendarTick]);
  const reloadCalendar = () => setCalendarTick((t) => t + 1);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  // Same label rule as every other PMS screen: the MAXACT sequence id is
  // internal plumbing, and the area prefix is dropped unless two activities
  // would then read the same. Honours the app-wide "show WBS codes" toggle.
  const activityLabel = useWbsRecordName(activities, "activityId", "activityName");

  // Phases come free with the activities call; picking one narrows both the
  // register and the activity list, which is what makes 50 faces navigable.
  const phaseOptions = useMemo(() => {
    const seen = new Map<number, string>();
    activities.forEach((a: any) => {
      if (a.phaseId && !seen.has(a.phaseId)) {
        seen.set(a.phaseId, a.phaseName || `Phase ${a.phaseId}`);
      }
    });
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
  }, [activities]);

  // `name` is the plain label the search filters on; `label` may carry the
  // tracked-line badge, which a string filter could not read.
  const activityOptions = useMemo(
    () =>
      activities
        .filter((a: any) => !phaseFilter || a.phaseId === phaseFilter)
        .map((a: any) => {
          const name = activityLabel(a);
          const tracked = Number(a.trackedTaskCount) || 0;
          return {
            value: a.activityId,
            name,
            label:
              tracked > 0 ? (
                <span>
                  {name}
                  <Tooltip title={`${tracked} tracked line${tracked === 1 ? "" : "s"} on ${badgeDate}`}>
                    <span className="pms-exec-count-badge">{tracked}</span>
                  </Tooltip>
                </span>
              ) : (
                name
              ),
          };
        }),
    [activities, phaseFilter, activityLabel]
  );

  const configuredCount = useMemo(
    () => activities.filter((a) => a.isConfigured).length,
    [activities]
  );
  // Newest first, so page 1's first DATE group is the most recent capture.
  const lastCaptured = pageNo === 1 && dates.length ? dates[0].executionDate : null;
  const entryCount = useMemo(
    () =>
      dates.reduce(
        (acc: number, d: any) =>
          acc +
          (d.phases || []).reduce(
            (n: number, ph: any) => n + (ph.entries || []).length,
            0
          ),
        0
      ),
    [dates]
  );

  // The register is a TREE: date -> phase -> face. antd renders nested rows from
  // `children`, so the three levels are one dataSource with a `level` marker that
  // every column switches on.
  //
  // Keys are prefixed per level. A raw entryId would collide with a phaseId the
  // moment the two happened to share a number, and antd would then expand or
  // select the wrong row.
  const treeData = useMemo(
    () =>
      dates.map((d: any) => ({
        key: `d-${d.executionDate}`,
        level: "date",
        ...d,
        children: (d.phases || []).map((ph: any) => ({
          key: `d-${d.executionDate}-p-${ph.phaseId}`,
          level: "phase",
          executionDate: d.executionDate,
          ...ph,
          children: (ph.entries || []).map((e: any) => ({
            key: `e-${e.entryId}`,
            level: "entry",
            ...e,
          })),
        })),
      })),
    [dates]
  );

  // Only the LATEST date open on arrival (rows come newest-first), with its
  // phases; every older day starts collapsed — the register is a "what
  // happened most recently" view, and a page of fully open days pushed the
  // last capture below the fold. Earlier days are one click away.
  //
  // CONTROLLED, not `defaultExpandedRowKeys`: antd reads the default once, on
  // first mount, and this data arrives asynchronously. By the time the rows
  // exist the default has already been consumed, so every group would render
  // collapsed and the table would look empty.
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  useEffect(() => {
    const latest = treeData[0];
    setExpandedKeys(
      latest ? [latest.key, ...(latest.children || []).map((ph: any) => ph.key)] : []
    );
  }, [treeData]);
  const nothingConfigured = activities.length > 0 && configuredCount === 0;

  // Approval status of the days on this register page, and of the sheet date.
  const pageDates = useMemo(() => dates.map((d: any) => d.executionDate).filter(Boolean).sort(), [dates]);
  const registerApprovals = useDayApprovalStates(
    section === "civil" && !isCalendar ? projectId : undefined,
    pageDates[0] || "",
    pageDates[pageDates.length - 1] || ""
  );
  const sheetApprovals = useDayApprovalStates(section === "civil" || isCalendar ? undefined : projectId, sheetDate, sheetDate, reloadToken);
  const openDayReview = (date: string) =>
    navigate(`/pms/project/daily-approvals/review/${projectId}/${date}`, { state: location.state });

  const goToNorms = (activityId?: number) =>
    navigate(`/pms/execution-norms/${projectId}`, {
      state: { ...(location.state as any), ...(activityId ? { activityId } : {}) },
    });

  const openAdd = (date?: string) => {
    setEditing(null);
    setAddDate(date);
    setDrawerOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing({
      activityId: row.activityId,
      executionDate: row.executionDate,
      shiftId: row.shiftId || undefined,
    });
    setDrawerOpen(true);
  };

  const handleDelete = (row: any) => {
    Modal.confirm({
      title: "Delete this execution entry?",
      content: `${dayjs(row.executionDate).format("DD-MMM-YYYY")} · ${row.activityName}. Its ${row.derivedLineCount} derived BOQ quantities will be rolled back out of the monthly actuals.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await activityExecutionApis.deleteEntry(row.entryId);
        if (res?.status !== true) {
          message.error(res?.message || "Could not delete the entry");
          return;
        }
        message.success("Execution entry deleted");
        load();
      },
    });
  };

  // Leaving a sheet with unsaved lines — another section, another day — asks
  // first. The drafts live in the section and go with it.
  const guardUnsaved = (proceed: () => void) => {
    if (dirtyCount === 0) {
      proceed();
      return;
    }
    Modal.confirm({
      title: "Discard unsaved changes?",
      content: `${dirtyCount} task${dirtyCount === 1 ? " has" : "s have"} unsaved lines on this sheet.`,
      okText: "Discard",
      okButtonProps: { danger: true },
      onOk: () => {
        // The section may stay mounted (scope / phase / activity change), so
        // "Discard" has to drop the drafts itself, not rely on an unmount.
        sectionRef.current?.discardAll?.();
        proceed();
      },
    });
  };

  const switchView = (next: View) =>
    guardUnsaved(() => {
      setFocusState(null);
      updateQuery({ view: next === "calendar" ? "calendar" : null });
    });

  // From a calendar day to the sheet that fixes it, on that date.
  const openSheetFromCalendar = (next: "material" | "mechanical", date: string) => {
    setPickedDate(null);
    setSheetDate(date);
    updateQuery({ view: null, section: next });
  };

  // From the material upload's "Civil execution required" block: close the
  // dialog, land on Civil and start an entry on the date they were trying to
  // download — the day sheet is useless until that day has output to issue
  // against, so hand them the form rather than the tab.
  const recordCivilFor = (date: string) => {
    setUploadOpen(null);
    updateQuery({ view: null, section: "civil" });
    openAdd(date);
  };

  const openEntryFromCalendar = (entry: CalendarDayEntry, date: string) => {
    setEditing({ activityId: entry.activityId, executionDate: date, shiftId: entry.shiftId || undefined });
    setDrawerOpen(true);
  };

  const saveAll = async () => {
    if (!sectionRef.current || dirtyCount === 0) return;
    setSavingAll(true);
    try {
      await sectionRef.current.saveAll();
    } finally {
      setSavingAll(false);
    }
  };

  // One column set, three renderings. A row's `level` decides what it means:
  // a date summarises the day, a phase summarises its faces, a face is the
  // captured entry itself.
  const columns = [
    {
      title: "Date / Phase / Face",
      dataIndex: "executionDate",
      width: 300,
      render: (_: any, row: any) => {
        if (row.level === "date") {
          return (
            <span style={{ fontWeight: 700, color: "#022c57" }}>
              {dayjs(row.executionDate).format("DD-MMM-YYYY")}
              <span style={{ fontWeight: 400, color: "#8094ae", marginLeft: 8 }}>
                {row.faceCount} face{row.faceCount === 1 ? "" : "s"}
              </span>
            </span>
          );
        }
        if (row.level === "phase") {
          // splitWbsCode returns { label, code } — 'HRT - Adits Excavation' and
          // 'HEP-B1.7.1.1' — so the code shows as a chip instead of cluttering
          // the name, matching the Tasks tree.
          const { code, label } = splitWbsCode(row.phaseName || "");
          return (
            <span style={{ color: "#364a63" }}>
              {label || `Phase ${row.phaseId}`}
              {code && (
                <Tag style={{ marginInlineStart: 6 }} color="default">
                  {code}
                </Tag>
              )}
            </span>
          );
        }
        return (
          <span>
            {activityLabel(row)}
            {/* A norm revision has been back-dated over this day, so its figures
                were derived under a version that no longer governs its date. The
                day is NOT silently corrected — a reported number moves only when
                someone decides it should — so it is flagged instead. */}
            {row.isStale && (
              <Tooltip title="Captured under an older norm version. Use Restate days on the Execution Norms screen to bring it in line.">
                <Tag color="orange" style={{ marginInlineStart: 6 }}>
                  Stale
                </Tag>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      title: "Shift",
      dataIndex: "shiftName",
      width: 150,
      render: (_: any, row: any) => {
        if (row.level !== "entry") return null;
        if (!row.shiftName && !row.startTime)
          return <span style={{ color: "#8094ae" }}>Whole day</span>;
        return (
          <span>
            {row.shiftName && <div style={{ color: "#364a63" }}>{row.shiftName}</div>}
            {row.startTime && row.endTime && (
              <div style={{ fontSize: 12, color: "#8094ae" }}>
                {row.startTime}–{row.endTime}
                {row.workHoursLabel ? ` · ${row.workHoursLabel}` : ""}
              </div>
            )}
          </span>
        );
      },
    },
    {
      title: "Executed Qty",
      dataIndex: "slots",
      width: 230,
      render: (slots: any[], row: any) =>
        row.level !== "entry" ? null : slots?.length ? (
          <Space size={4} wrap>
            {slots.map((slot) => (
              <Tag key={slot.slotKey} style={{ marginInlineEnd: 0 }}>
                {slot.slotLabel} {fmtQty(slot.actualExecutionQty)}
              </Tag>
            ))}
          </Space>
        ) : (
          <span style={{ color: "#8094ae" }}>—</span>
        ),
    },
    {
      title: "Till date",
      dataIndex: "tillDatePercent",
      width: 190,
      render: (percent: string, row: any) => {
        // '' means there is no plan to divide by. Showing 0% there would claim
        // nothing has been done, which is a different statement from "there is
        // nothing to measure against".
        if (!percent)
          return <span style={{ color: "#8094ae" }}>Not measurable</span>;
        const pct = Number(percent);
        return (
          <Tooltip
            title={`${
              row.level === "date"
                ? "Whole project"
                : row.level === "phase"
                  ? "This phase"
                  : "This face"
            } — ${pct.toFixed(2)}% complete through ${dayjs(
              row.executionDate
            ).format("DD-MMM-YYYY")}`}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Progress
                percent={Math.min(pct, 100)}
                showInfo={false}
                size="small"
                strokeColor={row.level === "date" ? "#2557d6" : "#5b9bd5"}
                style={{ flex: 1, marginBottom: 0 }}
              />
              <span
                className="tabular-nums"
                style={{ minWidth: 48, textAlign: "right", color: "#364a63" }}
              >
                {pct.toFixed(2)}%
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "BOQ lines",
      dataIndex: "derivedLineCount",
      width: 110,
      align: "right" as const,
      render: (value: number, row: any) => {
        if (row.level === "date") {
          return <span className="tabular-nums">{row.boqLineCount || 0}</span>;
        }
        // With Task Monitoring on, a face's day reads "9 / 24": the lines the
        // site is expected to measure, of everything the norms derived.
        if (row.level === "entry" && monitoringConfigured) {
          return (
            <Tooltip title="tracked lines / derived lines">
              <span className="tabular-nums">
                {row.trackedLineCount || 0}
                <span style={{ color: "#8094ae" }}> / </span>
                {value || 0}
              </span>
            </Tooltip>
          );
        }
        return <span className="tabular-nums">{value || 0}</span>;
      },
    },
    {
      title: "Day approval",
      key: "dayApproval",
      width: 300,
      render: (_: any, row: any) =>
        row.level !== "date" ? null : (
          <DayApprovalActions
            projectId={projectId}
            date={row.executionDate}
            state={registerApprovals.states.get(row.executionDate)}
            onChanged={() => {
              registerApprovals.reload();
              load();
            }}
          />
        ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      ellipsis: true,
      render: (value: string, row: any) =>
        row.level !== "entry" ? null : value ? (
          <Tooltip title={value.length > 24 ? value : ""}>
            <span>{value}</span>
          </Tooltip>
        ) : (
          <span style={{ color: "#8094ae" }}>—</span>
        ),
    },
    {
      title: "",
      width: 84,
      render: (_: any, row: any) =>
        row.level !== "entry" ? null : (
          <Space size={2}>
            <Tooltip title="Edit">
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                size="small"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(row)}
              />
            </Tooltip>
          </Space>
        ),
    },
  ];

  const isCivil = section === "civil";
  // The store sheet is PROJECT-scoped: no task tree, so no tracking scope and
  // no phase/activity narrowing apply to it.
  const isStock = section === "stock";
  const viewSwitch = (
    <Segmented
      value={view}
      onChange={(v) => switchView(v as View)}
      options={[
        { label: "Register", value: "register", icon: <UnorderedListOutlined /> },
        { label: "Calendar", value: "calendar", icon: <CalendarOutlined /> },
      ]}
    />
  );
  const sheetDay = dayjs(sheetDate);
  const sectionTitle = isCivil
    ? "Daily Execution"
    : section === "material"
      ? "Material-Stores"
      : isStock
        ? "Store-Stock"
        : section === "cost"
          ? "Mucking & PRW"
          : "Mechanical";

  // ---- per-section chrome --------------------------------------------------
  const civilStats = (
    <>
      <StatCard
        label="Entries captured"
        value={entryCount}
        icon={<CalendarOutlined />}
      />
      <StatCard
        label="Faces ready to report"
        value={`${configuredCount} of ${activities.length}`}
        icon={<SettingOutlined />}
        tone={nothingConfigured ? "onhold" : "brand"}
        onClick={() => goToNorms()}
      />
      <StatCard
        label="Last captured"
        value={lastCaptured ? dayjs(lastCaptured).format("DD MMM YYYY") : "—"}
        icon={<ReloadOutlined />}
      />
      {/* The project as it stands NOW, across every captured date — not
          just the days on this page, or paging back through history would
          appear to undo work. */}
      <StatCard
        label="Project till date"
        value={projectTotals.percent ? `${projectTotals.percent}%` : "—"}
        icon={<RiseOutlined />}
      />
    </>
  );

  const summary = calendarData?.summary;
  const toggleFocus = (state: string) => setFocusState((current) => (current === state ? null : state));
  const calendarStats = (
    <>
      <StatCard
        label="Working days so far"
        value={summary ? summary.workingDaysSoFar : "—"}
        icon={<CalendarOutlined />}
        loading={calendarLoading && !calendarData}
      />
      <StatCard
        label="Entered"
        value={summary ? `${summary.enteredDays}${summary.enteredPercent ? ` · ${summary.enteredPercent}%` : ""}` : "—"}
        icon={<CheckCircleOutlined />}
        tone={summary && summary.workingDaysSoFar > 0 && summary.notEnteredDays === 0 ? "completed" : "brand"}
        onClick={() => toggleFocus("entered")}
        loading={calendarLoading && !calendarData}
      />
      <StatCard
        label="Not entered"
        value={summary ? summary.notEnteredDays : "—"}
        icon={<WarningOutlined />}
        tone={summary && summary.notEnteredDays > 0 ? "onhold" : "brand"}
        onClick={() => toggleFocus("notEntered")}
        loading={calendarLoading && !calendarData}
      />
      <StatCard
        label="In review · Approved"
        value={summary ? `${summary.inReviewDays} · ${summary.approvedDays}` : "—"}
        icon={<RiseOutlined />}
        onClick={() => toggleFocus("review")}
        loading={calendarLoading && !calendarData}
      />
    </>
  );

  const sheetStats = (
    <>
      <StatCard
        label="Input tasks"
        value={sheetMeta.taskCount}
        icon={<UnorderedListOutlined />}
        loading={sheetMeta.loading && !sheetMeta.hasSheet}
      />
      <StatCard
        label="Recorded today"
        value={`${sheetMeta.recordedTaskCount} of ${sheetMeta.taskCount}`}
        icon={<CheckCircleOutlined />}
        tone={sheetMeta.hasSheet && sheetMeta.taskCount > 0 && sheetMeta.recordedTaskCount === sheetMeta.taskCount ? "completed" : "brand"}
        loading={sheetMeta.loading && !sheetMeta.hasSheet}
      />
      <StatCard
        label="Produced today"
        value={sheetMeta.producedCount}
        icon={<RiseOutlined />}
        loading={sheetMeta.loading && !sheetMeta.hasSheet}
      />
    </>
  );

  // The store has no tasks and no output, so the task-shaped StatCards would be
  // three wrong labels. SheetSectionMeta carries the store's counts in
  // taskCount / recordedTaskCount (see StoreStockSection).
  const stockStats = (
    <>
      <StatCard
        label="Materials in store"
        value={sheetMeta.taskCount}
        icon={<UnorderedListOutlined />}
        loading={sheetMeta.loading && !sheetMeta.hasSheet}
      />
      <StatCard
        label="Moved today"
        value={`${sheetMeta.recordedTaskCount} of ${sheetMeta.taskCount}`}
        icon={<CheckCircleOutlined />}
        loading={sheetMeta.loading && !sheetMeta.hasSheet}
      />
    </>
  );

  const civilToolbar = (
    <div className="pms-exec-bar">
      <Select
        value={phaseFilter}
        onChange={(v) => {
          setPhaseFilter(v);
          setActivityFilter(undefined);
          setPageNo(1);
        }}
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="All phases"
        style={{ width: 240 }}
        options={phaseOptions}
      />
      <Select
        value={activityFilter}
        onChange={(v) => {
          setActivityFilter(v);
          setPageNo(1);
        }}
        allowClear
        showSearch
        optionFilterProp="name"
        placeholder="All activities"
        style={{ width: 320 }}
        options={activityOptions}
      />
      <DatePicker.RangePicker
        value={range as any}
        onChange={(v) => {
          setRange(v as any);
          setPageNo(1);
        }}
        format="DD-MMM-YYYY"
      />
      <Button icon={<ReloadOutlined />} onClick={load} />
    </div>
  );

  const calendarToolbar = (
    <div className="pms-exec-bar">
      <Space.Compact>
        <Button icon={<LeftOutlined />} aria-label="Previous month" onClick={() => setCalendarMonth(shiftMonth(calendarMonth, -1))} />
        <DatePicker
          picker="month"
          value={dayjs(`${calendarMonth}-01`)}
          onChange={(v) => v && setCalendarMonth(v.format("YYYY-MM"))}
          allowClear={false}
          format="MMM YYYY"
          style={{ width: 130 }}
        />
        <Button icon={<RightOutlined />} aria-label="Next month" onClick={() => setCalendarMonth(shiftMonth(calendarMonth, 1))} />
      </Space.Compact>
      <Button onClick={() => setCalendarMonth(dayjs().format("YYYY-MM"))}>Today</Button>
      {/* "Entered" follows this: All = any section; one section = that section only. */}
      <Segmented
        value={calendarFocus}
        onChange={(v) => setCalendarFocus(v as CalendarSection)}
        options={[
          { label: "All", value: "all" },
          { label: "Civil", value: "civil" },
          { label: "Material-Stores", value: "material" },
          { label: "Mechanical", value: "mechanical" },
        ]}
      />
      <Select
        value={phaseFilter}
        onChange={(v) => {
          setPhaseFilter(v);
          setActivityFilter(undefined);
        }}
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="All phases"
        style={{ width: 220 }}
        options={phaseOptions}
      />
      <Select
        value={activityFilter}
        onChange={(v) => setActivityFilter(v)}
        allowClear
        showSearch
        optionFilterProp="name"
        placeholder="All activities"
        style={{ width: 280 }}
        options={activityOptions}
      />
      <Button icon={<ReloadOutlined />} aria-label="Reload" onClick={reloadCalendar} />
    </div>
  );

  // Material, Mechanical and Mucking & PRW share this bar — same scope, same
  // phase/activity narrowing. No date: entries are stamped with today and
  // booked against the civil-completed task they are picked on.
  const sheetToolbar = (
    <div className="pms-exec-bar">
      <Tooltip title="Entries are recorded for today and counted against the civil work of the task you book them on">
        <Tag color="blue" style={{ marginInlineEnd: 0, padding: "4px 10px" }}>
          Today · {sheetDay.format("DD-MMM-YYYY")}
        </Tag>
      </Tooltip>
      {/* Tracked · All exists only where Task Monitoring is configured; the
          server applies ALL regardless when it is not. */}
      {sheetMeta.monitoringConfigured && (
        <Segmented
          value={sheetScope}
          onChange={(v) => guardUnsaved(() => setSheetScope(v as DaySheetScope))}
          options={[
            { label: "Tracked", value: "MONITORED" },
            { label: "All", value: "ALL" },
          ]}
        />
      )}
      <Select
        value={phaseFilter}
        onChange={(v) =>
          guardUnsaved(() => {
            setPageNo(1);
            setPhaseFilter(v);
            setActivityFilter(undefined);
          })
        }
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="All phases"
        style={{ width: 240 }}
        options={phaseOptions}
      />
      <Select
        value={activityFilter}
        onChange={(v) =>
          guardUnsaved(() => {
            setPageNo(1);
            setActivityFilter(v);
          })
        }
        allowClear
        showSearch
        optionFilterProp="name"
        placeholder="All activities"
        style={{ width: 320 }}
        options={activityOptions}
      />
      <Button icon={<ReloadOutlined />} onClick={() => setReloadToken((t) => t + 1)} />
      {sheetMeta.dayLocked && (
        <Tag style={{ marginInlineStart: 4 }} color="default">
          Day locked
        </Tag>
      )}
    </div>
  );

  // ONE strip for the whole tab: the day (or the range), the search, the
  // Entry/Day-wise switch and — in Entry — the day's approval actions. They used
  // to be two bordered bands that repeated the date between them and left the
  // sheet less room than the chrome above it.
  const stockToolbar = (
    <div className="pms-exec-bar pms-exec-bar--stock">
      {/* One date for both views: the sheet records it, the day-wise list reads
          it back. Everything else on this page is a single day, and a second
          date control to keep in step with it would be one too many. */}
      <DatePicker
        value={sheetDay}
        onChange={(v) => v && guardUnsaved(() => setSheetDate(toDateOnly(v.toDate())))}
        allowClear={false}
        format="DD-MMM-YYYY"
        disabledDate={(d) => d.isAfter(dayjs(), "day")}
        style={{ width: 165 }}
      />
      <Input
        allowClear
        className="pms-stock-search"
        prefix={<SearchOutlined style={{ color: "#8094ae" }} />}
        placeholder="Find a material, code or category"
        value={stockSearch}
        onChange={(e) => setStockSearch(e.target.value)}
      />
      <Segmented
        value={stockView}
        onChange={(v) => guardUnsaved(() => setStockView(v as "entry" | "register"))}
        options={[
          { label: "Entry", value: "entry" },
          { label: "Day-wise", value: "register" },
        ]}
      />
      <Button icon={<ReloadOutlined />} onClick={() => setReloadToken((t) => t + 1)} />
      {stockView === "entry" && sheetMeta.dayLocked && (
        <Tag color="default">Day locked</Tag>
      )}
      {stockView === "entry" && (
        <>
          <span className="pms-exec-bar-spacer" />
          <DayApprovalBar
            inline
            projectId={projectId}
            date={sheetDate}
            state={sheetApprovals.states.get(sheetDate)}
            onChanged={() => {
              sheetApprovals.reload();
              setReloadToken((t) => t + 1);
            }}
          />
        </>
      )}
    </div>
  );

  return (
    <>
      <ProjectPageShell
        headTitle={isCivil ? "Daily Execution" : `Daily Execution · ${sectionTitle}`}
        breadcrumb={[
          { label: projectName, onClick: () => navigate(-1) },
          { label: "Daily Execution" },
        ]}
        eyebrow="Execution & Actuals"
        title="Daily Execution"
        onBack={() => navigate(-1)}
        actions={
          isCalendar ? (
            <Space>
              {viewSwitch}
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openAdd()}>
                Add Execution
              </Button>
            </Space>
          ) : isCivil ? (
            <Space>
              {viewSwitch}
              <Button icon={<UploadOutlined />} onClick={() => setUploadOpen("civil")}>
                Upload Excel
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openAdd()}>
                Add Execution
              </Button>
            </Space>
          ) : (
            <Space>
              {viewSwitch}
              {/* Unsaved lines on the sheet would be overwritten by the reload
                  that follows an upload — ask first, as a day change does. */}
              <Button icon={<UploadOutlined />} onClick={() => guardUnsaved(() => setUploadOpen(section))}>
                Upload Excel
              </Button>
              <Tooltip title="The whole day as the client's ABS cost abstract — one sheet per BOQ task">
                <Button icon={<DownloadOutlined />} loading={absBusy} onClick={downloadAbs}>
                  Download ABS
                </Button>
              </Tooltip>
              <Tooltip title={sheetMeta.dayLocked ? "This day is locked" : dirtyCount ? "" : "Nothing changed yet"}>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={saveAll}
                  disabled={dirtyCount === 0 || sheetMeta.dayLocked}
                  loading={savingAll}
                >
                  Save all changed ({dirtyCount})
                </Button>
              </Tooltip>
            </Space>
          )
        }
        stats={isCalendar ? calendarStats : isCivil ? civilStats : isStock ? stockStats : sheetStats}
        tabs={
          isCalendar ? undefined : (
          <Tabs
            className="pms-tabs pms-exec-tabs"
            activeKey={section}
            onChange={(key) => guardUnsaved(() => setSection(key as Section))}
            items={[
              { key: "civil", label: "Civil" },
              { key: "material", label: "Material-Stores" },
              { key: "mechanical", label: "Mechanical" },
              { key: "stock", label: "Store-Stock" },
              { key: "cost", label: "Mucking & PRW" },
            ]}
          />
          )
        }
        toolbar={isCalendar ? calendarToolbar : isCivil ? civilToolbar : isStock ? stockToolbar : sheetToolbar}
      >
        {isCalendar ? (
          <>
            {calendarError && <Alert type="error" showIcon message={calendarError} style={{ marginBottom: 16 }} />}
            {calendarData?.projectStart && calendarMonth < calendarData.projectStart.slice(0, 7) && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`The project starts on ${dayjs(calendarData.projectStart).format("DD-MMM-YYYY")} — nothing is expected before then.`}
              />
            )}
            <ExecutionCalendar
              month={calendarMonth}
              data={calendarData}
              loading={calendarLoading}
              focusState={focusState}
              onPick={setPickedDate}
            />
            {calendarData?.calendarTitle && (
              <div className="pms-cal-sub" style={{ marginTop: 8 }}>
                Working days from the project calendar “{calendarData.calendarTitle}”.
              </div>
            )}
          </>
        ) : isCivil ? (
          <>
            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

            <RejectedDaysAlert states={registerApprovals.states} onOpen={openDayReview} />

            {nothingConfigured && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="No activity has execution norms yet"
                description="Execution norms turn one executed quantity into every dependent BOQ quantity. Until an activity has them, it cannot report."
                action={
                  <Button size="small" type="primary" onClick={() => goToNorms()}>
                    Configure norms
                  </Button>
                }
              />
            )}

            <Table
              size="small"
              rowKey="key"
              loading={loading}
              dataSource={treeData}
              columns={columns as any}
              scroll={{ x: 1500 }}
              className="pms-exec-register"
              // Keyed on the page's own dates so a page change re-opens the new
              // groups rather than leaving them collapsed.
              expandable={{
                expandedRowKeys: expandedKeys,
                onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
                indentSize: 18,
              }}
              rowClassName={(row: any) => `pms-exec-row-${row.level}`}
              // A single click on an entry opens it. Clicks on the row's own
              // controls (Edit/Delete, expand icon) or bubbling up from a
              // portal (confirm modal, popover) are left alone.
              onRow={(row: any) => ({
                onClick: (e: React.MouseEvent<HTMLElement>) => {
                  if (row.level !== "entry") return;
                  const target = e.target as HTMLElement;
                  if (!e.currentTarget.contains(target)) return;
                  if (target.closest("button, a, input, .ant-table-row-expand-icon")) return;
                  openEdit(row);
                },
              })}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      range || activityFilter || phaseFilter
                        ? "No execution captured in this range"
                        : "No execution captured yet"
                    }
                  >
                    {!nothingConfigured && (
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => openAdd()}>
                        Add Execution
                      </Button>
                    )}
                  </Empty>
                ),
              }}
              pagination={{
                current: pageNo,
                pageSize: PAGE_SIZE,
                total,
                onChange: setPageNo,
                showSizeChanger: false,
                // Days, because that is what a page holds now.
                showTotal: (t) => `${t} day${t === 1 ? "" : "s"} captured`,
              }}
            />
          </>
        ) : section === "material" ? (
          <>
          <DayApprovalBar
            projectId={projectId}
            date={sheetDate}
            state={sheetApprovals.states.get(sheetDate)}
            onChanged={() => {
              sheetApprovals.reload();
              setReloadToken((t) => t + 1);
            }}
          />
          <MaterialStoresSection
            ref={sectionRef}
            projectId={projectId}
            date={sheetDate}
            scope={sheetScope}
            phaseId={phaseFilter}
            activityId={activityFilter}
            reloadToken={reloadToken}
            uomOptions={uomOptions}
            onSheetChange={setSheetMeta}
            onDirtyChange={setDirtyCount}
          />
          </>
        ) : isStock ? (
          // No second band: the day's approval actions are in the strip above.
          stockView === "register" ? (
            <StockRegisterSection
              projectId={projectId}
              projectName={projectName}
              fromDate={sheetDate}
              toDate={sheetDate}
              search={stockSearch}
              reloadToken={reloadToken}
            />
          ) : (
            <StoreStockSection
              ref={sectionRef}
              projectId={projectId}
              date={sheetDate}
              search={stockSearch}
              reloadToken={reloadToken}
              onSheetChange={setSheetMeta}
              onDirtyChange={setDirtyCount}
            />
          )
        ) : (
          <>
          <DayApprovalBar
            projectId={projectId}
            date={sheetDate}
            state={sheetApprovals.states.get(sheetDate)}
            onChanged={() => {
              sheetApprovals.reload();
              setReloadToken((t) => t + 1);
            }}
          />
          {section === "cost" ? (
            <MuckingPrwSection
              ref={sectionRef}
              projectId={projectId}
              date={sheetDate}
              scope={sheetScope}
              phaseId={phaseFilter}
              activityId={activityFilter}
              reloadToken={reloadToken}
              onSheetChange={setSheetMeta}
              onDirtyChange={setDirtyCount}
            />
          ) : (
            <MechanicalSection
              ref={sectionRef}
              projectId={projectId}
              date={sheetDate}
              scope={sheetScope}
              phaseId={phaseFilter}
              activityId={activityFilter}
              reloadToken={reloadToken}
              onSheetChange={setSheetMeta}
              onDirtyChange={setDirtyCount}
            />
          )}
          </>
        )}
      </ProjectPageShell>

      {/* Mounted as a SIBLING of the shell so its `loading` state can never
          unmount the drawer mid-entry. */}
      <DailyExecutionDrawer
        open={drawerOpen}
        projectId={projectId}
        editing={editing}
        initialDate={addDate}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => {
          load();
          if (isCalendar) reloadCalendar();
        }}
        onConfigureNorms={goToNorms}
      />

      <CalendarDayDrawer
        open={isCalendar && !!pickedDate}
        projectId={projectId}
        date={pickedDate}
        phaseId={phaseFilter}
        activityId={activityFilter}
        onClose={() => setPickedDate(null)}
        onAddExecution={(date) => openAdd(date)}
        onEditEntry={openEntryFromCalendar}
        onOpenSheet={openSheetFromCalendar}
        onChanged={reloadCalendar}
      />

      {/* Excel uploads — each tab's dialog; a commit reloads what the tab shows. */}
      <CivilUploadModal
        open={uploadOpen === "civil"}
        projectId={projectId}
        onClose={() => setUploadOpen(null)}
        onSuccess={() => load()}
      />
      <MaterialIssueUploadModal
        open={uploadOpen === "material"}
        projectId={projectId}
        initialDate={sheetDate}
        onClose={() => setUploadOpen(null)}
        onSuccess={() => setReloadToken((t) => t + 1)}
        onRecordCivil={recordCivilFor}
      />
      <EquipmentLogUploadModal
        open={uploadOpen === "mechanical"}
        projectId={projectId}
        initialDate={sheetDate}
        scope={sheetScope}
        onClose={() => setUploadOpen(null)}
        onSuccess={() => setReloadToken((t) => t + 1)}
        onRecordCivil={recordCivilFor}
      />
      {/* Store-Stock takes no onRecordCivil: a store receives and issues on
          days nothing was built, so Civil is not a pre-condition here. */}
      <StoreStockUploadModal
        open={uploadOpen === "stock"}
        projectId={projectId}
        initialDate={sheetDate}
        onClose={() => setUploadOpen(null)}
        onSuccess={() => setReloadToken((t) => t + 1)}
      />
    </>
  );
};

export default DailyExecutionPage;
